export interface Video {
  id: string;
  title: string;
  /** ISO date, YYYY-MM-DD */
  tripDate: string;
  durationSec: number;
  hasThumb: boolean;
  sizeBytes: number;
}

export interface Me {
  username: string;
  isAdmin: boolean;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: init.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(body?.error ?? 'Что-то пошло не так', res.status);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export const api = {
  login: (username: string, password: string) =>
    request<Me>('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),

  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),

  me: () => request<Me>('/auth/me'),

  listVideos: () => request<Video[]>('/videos'),

  getVideo: (id: string) => request<Video>(`/videos/${id}`),

  /** Phase 2 of upload: attach metadata to already-transferred bytes. */
  saveVideo: (fields: { uploadId: string; title: string; tripDate: string; durationSec: number }) =>
    request<Video>('/videos', { method: 'POST', body: JSON.stringify(fields) }),

  /**
   * Admin edit. Passing `uploadId` replaces the video file itself with a
   * freshly transferred one; the server then deletes the file being replaced.
   */
  updateVideo: (
    id: string,
    fields: { title: string; tripDate: string; uploadId?: string; durationSec?: number },
  ) => request<Video>(`/videos/${id}`, { method: 'PATCH', body: JSON.stringify(fields) }),
};

export const videoStreamUrl = (id: string) => `/api/videos/${id}/stream`;
export const videoThumbUrl = (id: string) => `/api/videos/${id}/thumb`;

export interface PendingUpload {
  uploadId: string;
  sizeBytes: number;
}

/**
 * Phase 1 of upload: send the bytes, called as soon as a file is picked so the
 * transfer overlaps with the user typing a title.
 *
 * XHR rather than fetch: fetch has no upload-progress event, and a real 0-100%
 * bar during a multi-GB mobile upload is the whole point of this screen.
 */
export function uploadVideoFile(
  file: File,
  thumb: Blob | null,
  onProgress: (percent: number) => void,
): { promise: Promise<PendingUpload>; abort: () => void } {
  const form = new FormData();
  if (thumb) form.append('thumb', thumb, 'thumb.jpg');
  form.append('file', file, file.name);

  const xhr = new XMLHttpRequest();
  const promise = new Promise<PendingUpload>((resolve, reject) => {
    xhr.open('POST', '/api/videos/upload');
    xhr.withCredentials = true;

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      let body: unknown = null;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        /* non-JSON error page from a proxy */
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve(body as PendingUpload);
      } else {
        const message =
          (body as { error?: string } | null)?.error ??
          (xhr.status === 413 ? 'Файл слишком большой' : 'Не удалось загрузить видео');
        reject(new ApiError(message, xhr.status));
      }
    });

    xhr.addEventListener('error', () =>
      reject(new ApiError('Соединение прервано. Попробуйте ещё раз', 0)),
    );
    xhr.addEventListener('abort', () => reject(new ApiError('Загрузка отменена', 0)));

    xhr.send(form);
  });

  return { promise, abort: () => xhr.abort() };
}
